<template>
  <div class="contract-partya-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('contractPartyaManage.list.title')" :subtitle="$t('contractPartyaManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.partyA" clearable
            :placeholder="$t('contractPartyaManage.search.partyA')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.aContacts" clearable
            :placeholder="$t('contractPartyaManage.search.aContacts')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.aLink" clearable
            :placeholder="$t('contractPartyaManage.search.aLink')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('contractPartyaManage.list.title') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAdd">
            {{ $t('common.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('contractPartyaManage.page.empty')" @retry="getList">
        <el-table-column prop="partyA" :label="$t('contractPartyaManage.table.partyA')" align="left" min-width="180" show-overflow-tooltip />
        <el-table-column prop="aContacts" :label="$t('contractPartyaManage.table.aContacts')" align="center" width="140" />
        <el-table-column prop="aLink" :label="$t('contractPartyaManage.table.aLink')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.aLink || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="180" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <add-contract-partya ref="addContractPartya" @success="handleSuccess" />
    <edit-contract-partya ref="editContractPartya" @success="handleSuccess" />
    <delete-contract-partya ref="deleteContractPartya" @success="handleSuccess" />
  </div>
</template>

<script>
import { queryContractPartya } from '@/api/contract/contractPartyaManageApi'
import AddContractPartya from '@/components/contract/addContractPartya'
import EditContractPartya from '@/components/contract/editContractPartya'
import DeleteContractPartya from '@/components/contract/deleteContractPartya'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ContractPartyaManageList',
  components: {
    AddContractPartya,
    EditContractPartya,
    DeleteContractPartya,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        partyA: '',
        aContacts: '',
        aLink: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          partyA: this.searchForm.partyA,
          aContacts: this.searchForm.aContacts,
          aLink: this.searchForm.aLink,
          communityId: this.communityId
        }
        const { data, total } = await queryContractPartya(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('获取甲方列表失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('contractPartyaManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        partyA: '',
        aContacts: '',
        aLink: ''
      }
      this.handleSearch()
    },
    handleAdd() {
      this.$refs.addContractPartya.open()
    },
    handleEdit(row) {
      this.$refs.editContractPartya.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteContractPartya.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.contract-partya-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
